import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import {
  IStorageService,
  FileMetadata,
  StoredFile,
  GalleryItem,
  SessionInfo,
  StorageInfo,
  AppointmentLocal,
} from './storage.service';
import { FileSystemAccessService } from './file-system-access.service'; // ⭐ NUEVO
import { DeviceIdService } from '../device-id.service'; // ⭐ AGREGAR ESTE IMPORT

// ========== INTERFACES EXTENDIDAS ==========

export interface FileRecord {
  // ⭐ Agregar "export"
  key: string;
  blob: Blob;
  metadata: FileMetadata;
  createdAt: number;
}

interface SessionRecord {
  id: string;
  name: string;
  timestamp: number;
  fileCount: number;
  gallery: GalleryItem[];
  createdAt: number;
}

// ⭐ Usar la interfaz exportada como tipo base
type AppointmentLocalRecord = AppointmentLocal;

// ========== DATABASE CON NUEVA TABLA ==========

class RentasIoTDatabase extends Dexie {
  files!: Table<FileRecord, string>;
  sessions!: Table<SessionRecord, string>;
  appointments_local!: Table<AppointmentLocalRecord, string>;

  constructor() {
    super('RentasIoTDB');

    // ⭐ MANTENER VERSIONES ANTERIORES (no eliminar)
    this.version(2).stores({
      files: 'key, metadata.appointmentId, metadata.uploaded, metadata.timestamp',
      sessions: 'id, timestamp',
      appointments_local: '_localId, _serverId, _syncStatus, createdAt',
    });

    // ⭐ AGREGAR VERSIÓN ACTUAL (incrementar a la siguiente)
    this.version(211).stores({
      files: 'key, metadata.appointmentId, metadata.uploaded, metadata.timestamp, metadata.deviceId',
      sessions: 'id, timestamp',
      appointments_local: '_localId, _serverId, _syncStatus, createdAt',
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class WebStorageService implements IStorageService {
  private db!: RentasIoTDatabase;
  private currentSessionId: string | null = null;
  private fileSystemService: FileSystemAccessService; // ⭐ NUEVO
  private deviceIdService: DeviceIdService; // ⭐ AGREGAR ESTA PROPIEDAD

  constructor(deviceIdService: DeviceIdService) {
    // ⭐ MODIFICAR CONSTRUCTOR
    this.fileSystemService = new FileSystemAccessService();
    this.deviceIdService = deviceIdService; // ⭐ AGREGAR ESTA LÍNEA
  }

  async initialize(): Promise<void> {
    try {
      this.db = new RentasIoTDatabase();
      await this.db.open();
      console.log('✅ IndexedDB inicializado correctamente');

      // Verificar espacio disponible
      const info = await this.getStorageInfo();
      console.log(`📊 Espacio usado: ${(info.used / 1024 / 1024).toFixed(2)} MB`);

      // ⭐ NUEVO: Inicializar File System Access
      await this.initializeFileSystemAccess();
    } catch (error) {
      console.error('❌ Error inicializando IndexedDB:', error);
      throw error;
    }
  }

  /**
   * ⭐ NUEVO: Espera a que la base de datos esté inicializada
   */
  async waitForDB(): Promise<void> {
    if (this.db) {
      return; // Ya está inicializada
    }

    console.log('⏳ Esperando inicialización de BD...');

    // Esperar hasta que esté lista (máximo 5 segundos)
    const maxWait = 5000;
    const startTime = Date.now();

    while (!this.db && Date.now() - startTime < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (!this.db) {
      console.error('❌ Timeout esperando inicialización de BD');
      throw new Error('Base de datos no inicializada después de 5 segundos');
    }

    console.log('✅ Base de datos lista');
  }

  /**
   * ⭐ NUEVO: Inicializa File System Access API
   */
  private async initializeFileSystemAccess(): Promise<void> {
    if (!this.fileSystemService.isSupported()) {
      console.log('ℹ️ File System Access API no soportado en este navegador');
      console.log('   Los archivos solo se guardarán en IndexedDB');
      return;
    }

    // Verificar si ya hay permiso guardado
    const hasAccess = await this.fileSystemService.hasDirectoryAccess();

    if (hasAccess) {
      const dirInfo = await this.fileSystemService.getDirectoryInfo();
      console.log(`✅ Acceso a carpeta física: ${dirInfo?.name}`);
    } else {
      console.log('ℹ️ No hay permiso para carpeta física');
      console.log('   Se solicitará permiso en el primer guardado');
    }
  }

  async saveFile(key: string, blob: Blob, metadata: FileMetadata): Promise<string> {
    try {
      // ⭐ Verificar que blob es válido
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('El blob no es válido');
      }

      // ⭐ IMPORTANTE: Siempre obtener y usar el Device ID de este equipo
      // (ignorar cualquier deviceId que pueda venir en metadata)
      const deviceId = this.deviceIdService.getDeviceId();

      console.log(`💾 [saveFile] Guardando ${key}:`, {
        type: blob.type,
        size: blob.size,
        appointmentId: metadata.appointmentId,
        deviceId, // ⭐ Log del deviceId
      });

      // ========== 1. GUARDAR EN INDEXEDDB (SIEMPRE) ==========
      const record: FileRecord = {
        key,
        blob,
        metadata: {
          ...metadata,
          deviceId, // ⭐ SIEMPRE sobreescribir con el deviceId actual
          savedToFileSystem: false,
          fileSystemPath: undefined,
        },
        createdAt: Date.now(),
      };

      await this.db.files.put(record);

      // Crear URL para el blob
      const url = URL.createObjectURL(blob);

      console.log(`✅ [saveFile] Archivo guardado en IndexedDB: ${key} (${(blob.size / 1024).toFixed(2)} KB)`);

      // ========== 2. GUARDAR EN CARPETA FÍSICA (SI HAY PERMISO) ==========
      await this.saveToFileSystem(key, blob, metadata);

      return url;
    } catch (error) {
      console.error('❌ [saveFile] Error guardando archivo:', error);
      throw error;
    }
  }

  private async saveToFileSystem(key: string, blob: Blob, metadata: FileMetadata): Promise<void> {
    // Verificar si File System Access está disponible
    if (!this.fileSystemService.isSupported()) {
      return; // No hacer nada si no está soportado
    }

    try {
      // Verificar si hay permiso
      let hasAccess = await this.fileSystemService.hasDirectoryAccess();

      // Si no hay permiso, solicitarlo (solo la primera vez)
      if (!hasAccess) {
        console.log('📁 Solicitando permiso para carpeta física...');
        hasAccess = await this.fileSystemService.requestDirectoryAccess();

        if (!hasAccess) {
          console.log('ℹ️ Usuario no concedió permiso para carpeta física');
          return; // Usuario canceló, solo guardar en IndexedDB
        }
      }

      // ⭐ NUEVO: Organizar por procedimiento
      // Estructura: REM-{appointmentId}/fotos/ o REM-{appointmentId}/videos/
      const appointmentFolder = `REM-${metadata.appointmentId}`;
      const typeFolder = metadata.type === 'photo' ? 'fotos' : 'videos';
      const subfolder = `${appointmentFolder}/${typeFolder}`;

      const extension = metadata.type === 'photo' ? '.jpg' : '.mp4';
      const fileName = `${key}${extension}`;
      const relativePath = `${subfolder}/${fileName}`;

      console.log(`📁 Guardando en estructura: ${relativePath}`);

      // Guardar archivo en la estructura de carpetas
      const saved = await this.fileSystemService.saveFile(fileName, blob, subfolder);

      if (saved) {
        // Actualizar metadata en IndexedDB
        const record = await this.db.files.get(key);
        if (record) {
          record.metadata.savedToFileSystem = true;
          record.metadata.fileSystemPath = relativePath;
          await this.db.files.put(record);

          console.log(`✅ Archivo guardado en carpeta física: ${relativePath}`);
        }
      }
    } catch (error) {
      // No lanzar error, solo loggearlo
      // El archivo ya está en IndexedDB, que es lo importante
      console.warn('⚠️ No se pudo guardar en carpeta física:', error);
    }
  }

  async getFile(key: string): Promise<string | null> {
    try {
      // ⭐ NUEVO: Esperar a que la BD esté lista
      await this.waitForDB();

      if (!this.db) {
        console.error('❌ [getFile] Base de datos no inicializada');
        return null;
      }

      const record = await this.db.files.get(key);

      if (!record) {
        console.warn(`⚠️ [getFile] Archivo no encontrado: ${key}`);
        return null;
      }

      // ⭐ NUEVO: Verificar que el blob existe
      if (!record.blob) {
        console.error(`❌ [getFile] Registro encontrado pero sin blob: ${key}`);
        console.error('   Metadata:', record.metadata);
        console.error('   CreatedAt:', new Date(record.createdAt).toLocaleString());
        return null;
      }

      // Verificar que el blob es válido
      if (!(record.blob instanceof Blob)) {
        console.error(`❌ [getFile] El blob no es un objeto Blob válido: ${key}`);
        return null;
      }

      // Crear URL para el blob
      const url = URL.createObjectURL(record.blob);
      console.log(`✅ [getFile] Blob URL generada para ${key}: ${url}`);
      return url;
    } catch (error) {
      console.error(`❌ [getFile] Error obteniendo archivo ${key}:`, error);
      return null;
    }
  }

  async getMetadata(key: string): Promise<FileMetadata | null> {
    try {
      const record = await this.db.files.get(key);
      return record ? record.metadata : null;
    } catch (error) {
      console.error('❌ Error obteniendo metadata:', error);
      return null;
    }
  }

  async listFiles(sessionId: string): Promise<StoredFile[]> {
    try {
      const records = await this.db.files.where('metadata.sessionId').equals(sessionId).toArray();

      const files: StoredFile[] = records.map((record) => ({
        key: record.key,
        metadata: record.metadata,
        url: URL.createObjectURL(record.blob),
      }));

      return files;
    } catch (error) {
      console.error('❌ Error listando archivos:', error);
      return [];
    }
  }

  async deleteFile(key: string): Promise<void> {
    try {
      await this.db.files.delete(key);
      console.log(`🗑️ Archivo eliminado: ${key}`);
    } catch (error) {
      console.error('❌ Error eliminando archivo:', error);
      throw error;
    }
  }

  async createSession(): Promise<string> {
    try {
      const now = new Date();
      const sessionId = `sesion_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate(),
      ).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(
        2,
        '0',
      )}-${String(now.getSeconds()).padStart(2, '0')}`;

      const session: SessionRecord = {
        id: sessionId,
        name: sessionId,
        timestamp: now.getTime(),
        fileCount: 0,
        gallery: [],
        createdAt: now.getTime(),
      };

      await this.db.sessions.put(session);
      this.currentSessionId = sessionId;

      console.log(`✅ Sesión creada: ${sessionId}`);
      return sessionId;
    } catch (error) {
      console.error('❌ Error creando sesión:', error);
      throw error;
    }
  }

  async saveSessionMetadata(sessionId: string, gallery: GalleryItem[]): Promise<void> {
    try {
      const session = await this.db.sessions.get(sessionId);

      if (!session) {
        console.error('❌ Sesión no encontrada:', sessionId);
        return;
      }

      session.gallery = gallery;
      session.fileCount = gallery.length;

      await this.db.sessions.put(session);
      console.log(`✅ Metadata de sesión guardada: ${sessionId} (${gallery.length} items)`);
    } catch (error) {
      console.error('❌ Error guardando metadata de sesión:', error);
      throw error;
    }
  }

  async loadSessionMetadata(sessionId: string): Promise<GalleryItem[]> {
    try {
      const session = await this.db.sessions.get(sessionId);

      if (!session) {
        console.warn(`⚠️ Sesión no encontrada: ${sessionId}`);
        return [];
      }

      return session.gallery || [];
    } catch (error) {
      console.error('❌ Error cargando metadata de sesión:', error);
      return [];
    }
  }

  async listSessions(): Promise<SessionInfo[]> {
    try {
      const sessions = await this.db.sessions.orderBy('timestamp').reverse().toArray();

      return sessions.map((session) => ({
        id: session.id,
        name: session.name,
        timestamp: session.timestamp,
        displayName: this.formatSessionName(session.timestamp),
        fileCount: session.fileCount,
        isCurrent: session.id === this.currentSessionId,
      }));
    } catch (error) {
      console.error('❌ Error listando sesiones:', error);
      return [];
    }
  }

  async getStorageInfo(): Promise<StorageInfo> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const used = estimate.usage || 0;
        const total = estimate.quota || 0;
        const available = total - used;
        const percentage = total > 0 ? (used / total) * 100 : 0;

        return { used, available, total, percentage };
      }

      // Fallback: calcular tamaño aproximado de la base de datos
      const files = await this.db.files.toArray();
      const totalSize = files.reduce((acc, file) => acc + file.blob.size, 0);

      return {
        used: totalSize,
        available: 50 * 1024 * 1024, // 50 MB estimado
        total: 250 * 1024 * 1024, // 250 MB estimado
        percentage: (totalSize / (250 * 1024 * 1024)) * 100,
      };
    } catch (error) {
      console.error('❌ Error obteniendo info de almacenamiento:', error);
      return { used: 0, available: 0, total: 0, percentage: 0 };
    }
  }

  async cleanup(): Promise<void> {
    try {
      const MAX_AGE_DAYS = 30;
      const cutoffDate = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

      // Eliminar sesiones antiguas
      const oldSessions = await this.db.sessions.where('timestamp').below(cutoffDate).toArray();

      for (const session of oldSessions) {
        // Eliminar archivos de la sesión
        await this.db.files.where('metadata.sessionId').equals(session.id).delete();
        // Eliminar sesión
        await this.db.sessions.delete(session.id);
      }

      console.log(`🧹 Limpieza completada: ${oldSessions.length} sesiones antiguas eliminadas`);
    } catch (error) {
      console.error('❌ Error en limpieza:', error);
    }
  }

  private formatSessionName(timestamp: number): string {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${day}/${String(month).padStart(2, '0')}/${year} ${String(hours).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`;
  }

  // ========== NUEVOS MÉTODOS PARA SINCRONIZACIÓN ==========

  /**
   * ⭐ Obtiene archivos por appointmentId
   */
  async getFilesByAppointment(appointmentId: string): Promise<FileRecord[]> {
    return await this.db.files.where('metadata.appointmentId').equals(appointmentId).toArray();
  }

  /**
   * ⭐ MODIFICADO: Obtiene archivos pendientes de subir por appointmentId DE ESTE EQUIPO (SOLO FOTOS)
   */
  async getPendingFilesByAppointment(appointmentId: string): Promise<FileRecord[]> {
    const currentDeviceId = this.deviceIdService.getDeviceId();

    // ⚠️ MODIFICADO: Solo retornar FOTOS pendientes (videos NO se sincronizan)
    return await this.db.files
      .where('metadata.appointmentId')
      .equals(appointmentId)
      .and(
        (file) =>
          file.metadata.type === 'photo' && // ✅ SOLO FOTOS
          !file.metadata.uploaded &&
          file.metadata.deviceId === currentDeviceId,
      )
      .toArray();
  }

  /**
   * ⭐ Marca archivo como subido
   */
  async markFileAsUploaded(key: string, uploadUrl: string): Promise<void> {
    const file = await this.db.files.get(key);
    if (file) {
      file.metadata.uploaded = true;
      file.metadata.uploadUrl = uploadUrl;
      file.metadata.uploadedAt = Date.now();
      await this.db.files.put(file);
      console.log(`✅ Archivo marcado como subido: ${key}`);
    }
  }

  /**
   * ⭐ Guarda appointment local
   */
  async saveAppointmentLocal(appointment: AppointmentLocal): Promise<void> {
    await this.db.appointments_local.put(appointment);
    console.log(`✅ Appointment local guardado: ${appointment._localId}`);
  }

  /**
   * ⭐ Obtiene appointment local por ID
   */
  async getAppointmentLocal(localId: string): Promise<AppointmentLocal | undefined> {
    return await this.db.appointments_local.get(localId);
  }

  /**
   * ⭐ Obtiene todos los appointments pendientes de sincronizar
   */
  async getPendingAppointments(): Promise<AppointmentLocal[]> {
    return await this.db.appointments_local.where('_syncStatus').anyOf(['pending', 'error']).toArray();
  }

  /**
   * ⭐ Actualiza estado de sincronización de un appointment
   */
  async updateAppointmentSyncStatus(
    localId: string,
    status: 'pending' | 'syncing' | 'synced' | 'error',
    serverId?: string,
    error?: string,
  ): Promise<void> {
    const updates: Partial<AppointmentLocal> = {
      _syncStatus: status,
      updatedAt: Date.now(),
      _lastSyncAttempt: Date.now(),
    };

    if (serverId) updates._serverId = serverId;
    if (error) updates._syncError = error;
    if (status === 'synced') updates._pendingFiles = 0;

    await this.db.appointments_local.update(localId, updates);
    console.log(`✅ Appointment status actualizado: ${localId} -> ${status}`);
  }

  /**
   * ⭐ NUEVO: Obtiene información sobre File System Access
   */
  async getFileSystemInfo(): Promise<{
    supported: boolean;
    hasAccess: boolean;
    directoryName?: string;
  }> {
    const supported = this.fileSystemService.isSupported();

    if (!supported) {
      return { supported: false, hasAccess: false };
    }

    const hasAccess = await this.fileSystemService.hasDirectoryAccess();
    const dirInfo = await this.fileSystemService.getDirectoryInfo();

    return {
      supported,
      hasAccess,
      directoryName: dirInfo?.name,
    };
  }

  /**
   * ⭐ NUEVO: Solicita permiso manualmente para carpeta
   */
  async requestFileSystemAccess(): Promise<boolean> {
    if (!this.fileSystemService.isSupported()) {
      console.warn('⚠️ File System Access no soportado');
      return false;
    }

    return await this.fileSystemService.requestDirectoryAccess();
  }

  /**
   * ⭐ NUEVO: Limpia permiso de carpeta
   */
  async clearFileSystemAccess(): Promise<void> {
    await this.fileSystemService.clearStoredPermission();
  }

  /**
   * ⭐ MODIFICADO: Obtiene archivos pendientes de subir (solo fotos DE ESTE EQUIPO)
   */
  async getPendingPhotos(): Promise<FileRecord[]> {
    try {
      await this.waitForDB();

      if (!this.db || !this.db.files) {
        console.warn('⚠️ Base de datos no disponible');
        return [];
      }

      const currentDeviceId = this.deviceIdService.getDeviceId();

      return await this.db.files
        .filter(
          (file) =>
            file.metadata.type === 'photo' && !file.metadata.uploaded && file.metadata.deviceId === currentDeviceId, // ✅ Solo deviceId actual
        )
        .toArray();
    } catch (error) {
      console.error('❌ Error al obtener fotos pendientes:', error);
      return [];
    }
  }

  async getPendingVideos(): Promise<FileRecord[]> {
    // ⚠️ Los videos NO se suben a la nube, siempre retornar vacío
    console.log('📹 Videos NO se sincronizan (solo se guardan localmente)');
    return [];
  }

  /**
   * ⭐ MODIFICADO: Obtiene TODOS los archivos pendientes de subir DE ESTE EQUIPO (SOLO FOTOS)
   */
  async getAllPendingFiles(): Promise<FileRecord[]> {
    try {
      await this.waitForDB();

      if (!this.db || !this.db.files) {
        console.warn('⚠️ Base de datos no disponible');
        return [];
      }

      const currentDeviceId = this.deviceIdService.getDeviceId();

      // ⚠️ MODIFICADO: Solo retornar FOTOS pendientes (videos NO se sincronizan)
      return await this.db.files
        .filter(
          (file) =>
            file.metadata.type === 'photo' && // ✅ SOLO FOTOS
            !file.metadata.uploaded &&
            file.metadata.deviceId === currentDeviceId,
        )
        .toArray();
    } catch (error) {
      console.error('❌ Error al obtener archivos pendientes:', error);
      return [];
    }
  }

  /**
   * ⭐ NUEVO: Actualiza error de sincronización en un archivo
   */
  async updateFileSyncError(key: string, error: string): Promise<void> {
    const file = await this.db.files.get(key);
    if (file) {
      file.metadata.syncError = error;
      await this.db.files.put(file);
    }
  }

  /**
   * 🔍 DIAGNÓSTICO: Verifica la integridad de un archivo
   */
  async diagnoseFile(key: string): Promise<void> {
    console.log('🔍 [diagnoseFile] Diagnosticando:', key);

    try {
      const record = await this.db.files.get(key);

      if (!record) {
        console.error('❌ Registro no existe');
        return;
      }

      console.log('📋 Información del registro:', {
        key: record.key,
        hasBlob: !!record.blob,
        blobType: record.blob?.constructor?.name,
        blobSize: record.blob?.size,
        blobMimeType: record.blob?.type,
        metadata: record.metadata,
        createdAt: new Date(record.createdAt).toLocaleString(),
      });

      if (record.blob) {
        // Intentar crear URL
        try {
          const url = URL.createObjectURL(record.blob);
          console.log('✅ Blob URL creada correctamente:', url);
        } catch (e) {
          console.error('❌ Error creando blob URL:', e);
        }
      } else {
        console.error('❌ El registro NO tiene blob');
      }
    } catch (error) {
      console.error('❌ Error en diagnóstico:', error);
    }
  }

  /**
   * ⭐ NUEVO: Limpia archivos pendientes que no existen físicamente
   * Útil para eliminar archivos "fantasma" de otros equipos o borrados manualmente
   */
  async cleanupGhostFiles(): Promise<number> {
    try {
      await this.waitForDB();

      if (!this.db || !this.db.files) {
        console.warn('⚠️ Base de datos no disponible');
        return 0;
      }

      // Obtener todos los archivos pendientes DE ESTE EQUIPO
      const pendingFiles = await this.getAllPendingFiles();

      let deletedCount = 0;

      console.log(`🔍 Verificando ${pendingFiles.length} archivos pendientes...`);

      // Verificar cada archivo
      for (const fileRecord of pendingFiles) {
        try {
          // Intentar obtener el blob
          const blob = fileRecord.blob;

          // Verificar si el blob es válido
          if (!blob || !(blob instanceof Blob) || blob.size === 0) {
            console.warn(`⚠️ Archivo inválido o vacío: ${fileRecord.key}`);

            // Eliminar el registro
            await this.db.files.delete(fileRecord.key);
            deletedCount++;

            console.log(`🗑️ Archivo fantasma eliminado: ${fileRecord.key}`);
          }
        } catch (error) {
          console.error(`Error verificando archivo ${fileRecord.key}:`, error);
        }
      }

      console.log(`✅ Limpieza completada: ${deletedCount} archivos eliminados`);
      return deletedCount;
    } catch (error) {
      console.error('❌ Error en limpieza de archivos:', error);
      return 0;
    }
  }

  /**
   * ⭐ NUEVO: Elimina un archivo específico por su key
   */
  async deleteFileByKey(key: string): Promise<boolean> {
    try {
      await this.waitForDB();

      if (!this.db || !this.db.files) {
        console.warn('⚠️ Base de datos no disponible');
        return false;
      }

      await this.db.files.delete(key);
      console.log(`🗑️ Archivo eliminado: ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Error eliminando archivo ${key}:`, error);
      return false;
    }
  }
}
